import client from './client';
import qs from 'qs';

const agentUrl = '/api/v1/eqp/agent';

// ============================= 설비 모니터링 에이전트 =============================

export const getAgentList = (params) =>
  client.get(`${agentUrl}/list?${qs.stringify(params)}`);

export const addAgent = (data) =>
  client.post(`${agentUrl}/info`, data);

export const editAgent = (agentUuid, data) =>
  client.put(`${agentUrl}/info/${agentUuid}`, data);

// 토큰 재발급 - 이전 토큰은 즉시 무효화된다(PROTOCOL.md §1)
export const reissueAgentToken = (agentUuid) =>
  client.post(`${agentUrl}/token/${agentUuid}`);

export const removeAgent = (agentUuid) =>
  client.delete(`${agentUrl}/info/${agentUuid}`);

// 에이전트가 담당하는 장비 현황(상세 패널의 "담당 장비" 목록)
export const getAgentMachines = (agentUuid) =>
  client.get(`${agentUrl}/${agentUuid}/machines`);

// ===== 전송 보안 (PROTOCOL.md §6) =====

// 보안 경고 확인 처리(지움) - IP 변경 감시가 남긴 SEC_WARN을 비운다
export const clearAgentSecWarn = (agentUuid) =>
  client.post(`${agentUrl}/${agentUuid}/sec-warn/clear`);

// 최근 N일(기본 7일) 접속 IP 변경 로그(최신순)
export const getAgentIpLog = (agentUuid, days = 7) =>
  client.get(`${agentUrl}/${agentUuid}/ip-log?${qs.stringify({ days })}`);

// 응답 봉투 키가 화면마다 list/data/info 로 다르게 내려와서 셋 다 받아 준다
export const extractAgentList = (res) => res?.data?.list ?? res?.data?.data ?? res?.data?.info ?? [];
export const extractMachineList = (res) => res?.data?.list ?? res?.data?.data ?? res?.data?.info ?? [];
export const extractIpLogList = (res) => res?.data?.list ?? res?.data?.data ?? [];

// ============================= 주변 장비 찾기 (on-demand discovery) =============================

// 검색 요청 생성 - body: {subnets, ports} 둘 다 선택값(비우면 서버 기본값/에이전트 자동탐지)
export const startAgentDiscovery = (agentUuid, body) =>
  client.post(`${agentUrl}/${agentUuid}/discovery`, body || {});

// 최신 요청 + 결과 목록(다이얼로그가 3초마다 폴링) - 요청이 아예 없으면 info가 null로 온다
export const getLatestAgentDiscovery = (agentUuid) =>
  client.get(`${agentUrl}/${agentUuid}/discovery/latest`);

// 후보 등록 -> EQP_INF + EQP_CONN_INF(FOCAS) 생성. body: {eqpNm} 선택값
export const registerDiscoveryCandidate = (agentUuid, reqUuid, resultId, body) =>
  client.post(`${agentUrl}/${agentUuid}/discovery/${reqUuid}/results/${resultId}/register`, body || {});

// 검색 중지 - REQUESTED면 즉시 CANCELLED, RUNNING이면 CANCEL_REQUESTED(에이전트 ack 대기, 서버가 2분 타임아웃)
export const cancelAgentDiscovery = (agentUuid, reqUuid) =>
  client.post(`${agentUrl}/${agentUuid}/discovery/${reqUuid}/cancel`);

export const extractDiscovery = (res) => res?.data?.info ?? null;

// ============================= 설치 파일 배포 (agent-release) =============================

// 등록된 설치 파일 정보(버전·빌드일시·OS별 크기/sha256) - 아직 없으면 info가 null로 온다
export const getAgentRelease = () => client.get(`${agentUrl}/release`);
export const extractAgentRelease = (res) => res?.data?.info ?? null;

/**
 * 공용 설치 파일 다운로드 - blob + 인증(토큰은 client.js 인터셉터가 자동으로 붙인다).
 * onProgress(percent)를 넘기면 다운로드 진행률을 받을 수 있다(안 넘기면 그냥 무시됨).
 */
export const downloadAgentRelease = (os, onProgress) =>
  client.get(`${agentUrl}/release/download/${os}`, {
    responseType: 'blob',
    onDownloadProgress: onProgress
      ? (evt) => {
        if (evt.total) onProgress(Math.round((evt.loaded * 100) / evt.total));
      }
      : undefined,
  });

/**
 * 이 에이전트 전용 설치 파일 - 24시간·1회용 등록 코드(enroll.json)가 들어있다.
 * 응답 헤더 X-Enroll-Expires에 만료시각이 담겨 온다(안내용).
 */
export const downloadAgentInstaller = (agentUuid, os, onProgress) =>
  client.get(`${agentUrl}/${agentUuid}/installer/${os}`, {
    responseType: 'blob',
    onDownloadProgress: onProgress
      ? (evt) => {
        if (evt.total) onProgress(Math.round((evt.loaded * 100) / evt.total));
      }
      : undefined,
  });

// Content-Disposition에서 파일명을 꺼낸다(서버가 RFC 5987로 인코딩해서 내려준다) - 실패하면 fallback 사용
export const extractFileNameFromResponse = (res, fallback) => {
  const disposition = res?.headers?.['content-disposition'] || res?.headers?.['Content-Disposition'];
  if (!disposition) return fallback;
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  if (!match) return fallback;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

// 응답 헤더 X-Enroll-Expires(전용 설치 파일의 등록 코드 만료시각) - 없으면 null
export const extractEnrollExpires = (res) => res?.headers?.['x-enroll-expires'] || null;

// ============================= 자동 업데이트 (PROTOCOL.md §7, v1.5) =============================

// 자동 업데이트/버전 고정 설정은 기존 수정(editAgent) 폼에 autoUpdateYn/targetVer 필드를 같이 보내면 된다.

// 업데이트 이력(최신순) - 기본 20건
export const getAgentUpdateHistory = (agentUuid, limit = 20) =>
  client.get(`${agentUrl}/${agentUuid}/update-history?${qs.stringify({ limit })}`);

export const extractUpdateHistoryList = (res) => res?.data?.list ?? [];
